import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Feather } from "lucide-react";

export default function Onboarding() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ pseudonym: "", bio: "", age_attested: false, policy_accepted: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user === false)) nav("/auth", { replace: true });
    // If already onboarded (age attested + no needs_onboarding), skip
    if (user && user !== false && user.age_attested && !user.needs_onboarding) {
      nav("/dashboard", { replace: true });
    }
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.age_attested || !form.policy_accepted) {
      toast.error("Please confirm the age and policy attestations.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/complete-onboarding", form);
      await refresh();
      toast.success(`Welcome, ${form.pseudonym}.`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const chk = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  if (loading || !user || user === false) return <div className="min-h-screen grain"><Nav /></div>;

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-md mx-auto px-5 sm:px-8 py-14" data-testid="onboarding-page">
        <div className="flex items-center gap-2 mb-6 text-[#D9B472]">
          <Feather className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-widest font-mono-brass">One last thing</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F7F3EC] tracking-tight mb-3">Choose a pen name.</h1>
        <p className="font-serif-body text-[#A8A095] mb-8 italic">
          Signed in as <span className="text-[#D9B472]">{user.email}</span>. Your legal name and email stay private — only your pseudonym is ever public.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Pseudonym (public)</label>
            <input
              type="text"
              required
              minLength={2}
              maxLength={40}
              value={form.pseudonym}
              onChange={upd("pseudonym")}
              placeholder="e.g., The Cartographer"
              className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#D9B472] font-serif-display italic text-lg"
              data-testid="onboarding-pseudonym"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Bio (optional)</label>
            <textarea
              value={form.bio}
              onChange={upd("bio")}
              rows={2}
              className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#F7F3EC] font-serif-body text-sm resize-none"
              data-testid="onboarding-bio"
            />
          </div>

          <div className="mt-4 border border-[rgba(217,180,114,0.2)] rounded-sm p-4 space-y-3 bg-[rgba(217,180,114,0.03)]">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-[#D9B472]" />
              <span className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass">Content policy</span>
            </div>
            <label className="flex items-start gap-3 text-sm text-[#A8A095] font-serif-body leading-relaxed cursor-pointer">
              <input type="checkbox" required checked={form.age_attested} onChange={chk("age_attested")} className="mt-1 accent-[#D9B472]" data-testid="onboarding-age" />
              <span>I am <strong className="text-[#F7F3EC]">18 years or older</strong>, and any person depicted, described, or referenced sexually in the work I list is <strong className="text-[#F7F3EC]">at least 18 years old</strong>.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-[#A8A095] font-serif-body leading-relaxed cursor-pointer">
              <input type="checkbox" required checked={form.policy_accepted} onChange={chk("policy_accepted")} className="mt-1 accent-[#D9B472]" data-testid="onboarding-policy" />
              <span>I accept the <Link to="/policy" target="_blank" className="text-[#D9B472] underline">Content Policy</Link>. CSAM and non-consensual sexual content are strictly prohibited and result in immediate suspension.</span>
            </label>
          </div>

          <button type="submit" disabled={busy} className="btn-buy w-full rounded-sm py-3.5 font-mono-brass text-xs uppercase tracking-widest font-semibold" data-testid="onboarding-submit">
            {busy ? "Working…" : "Enter the archive"}
          </button>
        </form>
      </div>
    </div>
  );
}
